import React from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import { Box, CircularProgress, Typography } from '@mui/material';

// Material-UI 테마 설정
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

// 로딩 컴포넌트
const LoadingScreen: React.FC = () => (
  <Box 
    display="flex" 
    flexDirection="column"
    justifyContent="center" 
    alignItems="center" 
    minHeight="100vh"
    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
  >
    <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
    <Typography variant="h6" sx={{ color: 'white', opacity: 0.9 }}>
      시스템 로딩 중...
    </Typography>
  </Box>
);

// 권한 없음 화면
const UnauthorizedScreen: React.FC<{ user: any }> = ({ user }) => (
  <Box 
    display="flex" 
    flexDirection="column"
    justifyContent="center" 
    alignItems="center" 
    minHeight="100vh"
    p={3}
    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
  >
    <Box 
      sx={{ 
        textAlign: 'center',
        maxWidth: 500,
        p: 4,
        borderRadius: 3,
        bgcolor: 'white',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ color: '#e53e3e', fontWeight: 600 }}>
        접근 권한이 없습니다
      </Typography>
      <Typography variant="body1" paragraph sx={{ color: '#4a5568' }}>
        현재 계정 <strong>{user.email}</strong>은 시스템 접근 권한이 없습니다.
      </Typography>
      <Typography variant="body2" sx={{ color: '#718096', mb: 3 }}>
        관리자에게 문의하여 권한을 요청해주세요.
      </Typography>
      <Box sx={{ p: 2, bgcolor: '#f7fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
          📧 문의: mark4mission@gmail.com
        </Typography>
      </Box>
    </Box>
  </Box>
);

// 메인 앱 컨텐츠
const AppContent: React.FC = () => {
  const { user, loading, userRole } = useAuth();

  // 로딩 중
  if (loading) {
    return <LoadingScreen />;
  }

  // 로그인하지 않은 경우
  if (!user) {
    return <LoginPage />;
  }

  // 권한이 없는 경우
  if (!userRole) {
    return <UnauthorizedScreen user={user} />;
  }

  // 정상 로그인 및 권한 있음
  return <Dashboard />;
};

// 최상위 App 컴포넌트
const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
